import { useState, useRef, useEffect } from "react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Send } from "lucide-react";
import { cn } from "@/shared/lib/cn";

interface CommentInputProps {
  onSendComment: (text: string) => void;
  isCreating?: boolean;
}

export function CommentInput({
  onSendComment,
  isCreating = false,
}: CommentInputProps) {
  const [comment, setComment] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsCreatingRef = useRef(isCreating);

  useEffect(() => {
    if (prevIsCreatingRef.current && !isCreating) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
    prevIsCreatingRef.current = isCreating;
  }, [isCreating]);

  const handleSend = () => {
    if (comment.trim() && !isCreating) {
      onSendComment(comment.trim());
      setComment("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-grid-200 p-4">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          placeholder="Add a comment..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1"
          disabled={isCreating}
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSend}
          disabled={!comment.trim() || isCreating}
          aria-label="Send comment"
        >
          <Send className={cn("h-5 w-5", isCreating && "animate-pulse")} />
        </Button>
      </div>
    </div>
  );
}
